import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMapPin, FiPhone, FiMail, FiPackage } from 'react-icons/fi';
import { dealerService } from '../services/dealerService';

// Fix default marker icons (Leaflet + bundlers issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const activeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const selectedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Demo dealer data across multiple states
const DEMO_DEALERS = [
  // Texas — 4 remaining
  {
    id: 'demo-1',
    company_name: 'Lone Star Sheds',
    city: 'Dallas',
    state: 'TX',
    phone: '(214) 555-0142',
    email: 'info@lonestarsheds.com',
    lat: 32.7767,
    lng: -96.7970,
    radius_miles: 35,
    shed_count: 42,
    subscription_plan: 'enterprise',
    is_active: true,
  },
  {
    id: 'demo-2',
    company_name: 'Hill Country Structures',
    city: 'Austin',
    state: 'TX',
    phone: '(512) 555-0234',
    email: 'hello@hillcountrystructures.com',
    lat: 30.2672,
    lng: -97.7431,
    radius_miles: 40,
    shed_count: 35,
    subscription_plan: 'enterprise',
    is_active: true,
  },
  {
    id: 'demo-3',
    company_name: 'Gulf Coast Sheds',
    city: 'Houston',
    state: 'TX',
    phone: '(713) 555-0311',
    email: 'info@gulfcoastsheds.com',
    lat: 29.7604,
    lng: -95.3698,
    radius_miles: 45,
    shed_count: 51,
    subscription_plan: 'enterprise',
    is_active: true,
  },
  {
    id: 'demo-4',
    company_name: 'Alamo Storage Buildings',
    city: 'San Antonio',
    state: 'TX',
    phone: '(210) 555-0477',
    email: 'info@alamostorage.com',
    lat: 29.4241,
    lng: -98.4936,
    radius_miles: 35,
    shed_count: 30,
    subscription_plan: 'pro',
    is_active: true,
  },
  // North Dakota
  {
    id: 'demo-5',
    company_name: 'Prairie Built Sheds',
    city: 'Bismarck',
    state: 'ND',
    phone: '(701) 555-0288',
    email: 'info@prairiebuiltsheds.com',
    lat: 46.8083,
    lng: -100.7837,
    radius_miles: 50,
    shed_count: 19,
    subscription_plan: 'pro',
    is_active: true,
  },
  // South Dakota
  {
    id: 'demo-6',
    company_name: 'Black Hills Barns',
    city: 'Rapid City',
    state: 'SD',
    phone: '(605) 555-0344',
    email: 'sales@blackhillsbarns.com',
    lat: 44.0805,
    lng: -103.2310,
    radius_miles: 55,
    shed_count: 14,
    subscription_plan: 'basic',
    is_active: true,
  },
  // Colorado — 2
  {
    id: 'demo-7',
    company_name: 'Front Range Structures',
    city: 'Denver',
    state: 'CO',
    phone: '(303) 555-0419',
    email: 'info@frontrangestructures.com',
    lat: 39.7392,
    lng: -104.9903,
    radius_miles: 35,
    shed_count: 38,
    subscription_plan: 'enterprise',
    is_active: true,
  },
  {
    id: 'demo-8',
    company_name: 'Springs Shed Co.',
    city: 'Colorado Springs',
    state: 'CO',
    phone: '(719) 555-0521',
    email: 'hello@springsshedco.com',
    lat: 38.8339,
    lng: -104.8214,
    radius_miles: 30,
    shed_count: 24,
    subscription_plan: 'pro',
    is_active: true,
  },
  // Northern California
  {
    id: 'demo-9',
    company_name: 'NorCal Outdoor Buildings',
    city: 'Sacramento',
    state: 'CA',
    phone: '(916) 555-0637',
    email: 'info@norcaloutdoor.com',
    lat: 38.5816,
    lng: -121.4944,
    radius_miles: 40,
    shed_count: 33,
    subscription_plan: 'enterprise',
    is_active: true,
  },
  // Louisiana — 2
  {
    id: 'demo-10',
    company_name: 'Bayou Sheds & Barns',
    city: 'Baton Rouge',
    state: 'LA',
    phone: '(225) 555-0813',
    email: 'info@bayousheds.com',
    lat: 30.4515,
    lng: -91.1871,
    radius_miles: 35,
    shed_count: 26,
    subscription_plan: 'pro',
    is_active: true,
  },
  {
    id: 'demo-11',
    company_name: 'Crescent City Storage',
    city: 'New Orleans',
    state: 'LA',
    phone: '(504) 555-0927',
    email: 'sales@crescentcitystorage.com',
    lat: 29.9511,
    lng: -90.0715,
    radius_miles: 30,
    shed_count: 21,
    subscription_plan: 'pro',
    is_active: true,
  },
  // New Mexico
  {
    id: 'demo-12',
    company_name: 'Desert Sun Structures',
    city: 'Albuquerque',
    state: 'NM',
    phone: '(505) 555-0746',
    email: 'info@desertsunstructures.com',
    lat: 35.0844,
    lng: -106.6504,
    radius_miles: 45,
    shed_count: 17,
    subscription_plan: 'basic',
    is_active: true,
  },
];

// Fly to selected dealer
function FlyToDealer({ dealer }) {
  const map = useMap();
  useEffect(() => {
    if (dealer) {
      map.flyTo([dealer.lat, dealer.lng], 10, { duration: 1 });
    }
  }, [dealer, map]);
  return null;
}

const DealerMap = ({ compact = false, className = '' }) => {
  const [dealers, setDealers] = useState(DEMO_DEALERS);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);

  // Try to load real dealers from Supabase, fall back to demo
  useEffect(() => {
    const loadDealers = async () => {
      setLoading(true);
      try {
        const { data, error } = await dealerService.getAllDealers();
        if (!error && data && data.length > 0) {
          // Map real dealers — use demo coords for known Texas cities
          const cityCoords = {
            'dallas': { lat: 32.7767, lng: -96.7970 },
            'fort worth': { lat: 32.7555, lng: -97.3308 },
            'austin': { lat: 30.2672, lng: -97.7431 },
            'houston': { lat: 29.7604, lng: -95.3698 },
            'san antonio': { lat: 29.4241, lng: -98.4936 },
            'plano': { lat: 33.0198, lng: -96.6989 },
            'waco': { lat: 31.5493, lng: -97.1467 },
            'tyler': { lat: 32.3513, lng: -95.3011 },
            'amarillo': { lat: 35.2220, lng: -101.8313 },
            'lubbock': { lat: 33.5779, lng: -101.8552 },
            'el paso': { lat: 31.7619, lng: -106.4850 },
            'corpus christi': { lat: 27.8006, lng: -97.3964 },
            'mcallen': { lat: 26.2034, lng: -98.2300 },
            'midland': { lat: 31.9973, lng: -102.0779 },
            'arlington': { lat: 32.7357, lng: -97.1081 },
            'frisco': { lat: 33.1507, lng: -96.8236 },
            'mckinney': { lat: 33.1972, lng: -96.6397 },
            'denton': { lat: 33.2148, lng: -97.1331 },
          };
          const mapped = data
            .filter(d => d.is_active)
            .map(d => {
              const key = (d.city || '').toLowerCase();
              const coords = cityCoords[key];
              if (!coords) return null;
              return {
                ...d,
                lat: coords.lat,
                lng: coords.lng,
                radius_miles: 30,
                shed_count: Math.floor(Math.random() * 40) + 10,
              };
            })
            .filter(Boolean);

          if (mapped.length > 0) {
            setDealers(mapped);
          }
        }
      } catch (err) {
        console.log('Using demo dealer data for map');
      }
      setLoading(false);
    };
    loadDealers();
  }, []);

  const milesToMeters = (miles) => miles * 1609.34;

  const planBadge = (plan) => {
    const colors = {
      enterprise: 'bg-purple-100 text-purple-700',
      pro: 'bg-blue-100 text-blue-700',
      basic: 'bg-gray-100 text-gray-600',
    };
    return colors[plan] || colors.basic;
  };

  const totalSheds = dealers.reduce((sum, d) => sum + (d.shed_count || 0), 0);

  if (compact) {
    return (
      <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
        <MapContainer
          center={[38.5, -102]}
          zoom={4}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%', minHeight: 300 }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {dealers.map((dealer) => (
            <React.Fragment key={dealer.id}>
              <Circle
                center={[dealer.lat, dealer.lng]}
                radius={milesToMeters(dealer.radius_miles)}
                pathOptions={{
                  color: '#059669',
                  fillColor: '#059669',
                  fillOpacity: 0.08,
                  weight: 1,
                }}
              />
              <Marker position={[dealer.lat, dealer.lng]} icon={activeIcon}>
                <Popup>
                  <strong>{dealer.company_name}</strong>
                  <br />
                  {dealer.city}, {dealer.state}
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
    );
  }

  return (
    <div className={`flex flex-col lg:flex-row gap-0 bg-white rounded-lg overflow-hidden border border-gray-200 ${className}`}>
      {/* Sidebar */}
      <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 border-r border-gray-200 flex flex-col" style={{ maxHeight: compact ? 400 : 600 }}>
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900 text-lg">{dealers.length}</span> dealers &middot;{' '}
            <span className="font-semibold text-gray-900">{totalSheds}</span> sheds in network
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {dealers.map((dealer) => (
            <button
              key={dealer.id}
              onClick={() => setSelectedDealer(dealer)}
              className={`w-full text-left px-4 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedDealer?.id === dealer.id ? 'bg-emerald-50 border-l-2 border-l-emerald-600' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">{dealer.company_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <FiMapPin className="w-3 h-3" />
                    {dealer.city}, {dealer.state}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${planBadge(dealer.subscription_plan)}`}>
                  {dealer.subscription_plan}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <FiPackage className="w-3 h-3" />
                  {dealer.shed_count} sheds
                </span>
                <span>{dealer.radius_miles} mi radius</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: compact ? 400 : 600 }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-[1000] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        )}
        <MapContainer
          ref={mapRef}
          center={[38.5, -102]}
          zoom={5}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FlyToDealer dealer={selectedDealer} />
          {dealers.map((dealer) => (
            <React.Fragment key={dealer.id}>
              <Circle
                center={[dealer.lat, dealer.lng]}
                radius={milesToMeters(dealer.radius_miles)}
                pathOptions={{
                  color: selectedDealer?.id === dealer.id ? '#dc2626' : '#059669',
                  fillColor: selectedDealer?.id === dealer.id ? '#dc2626' : '#059669',
                  fillOpacity: selectedDealer?.id === dealer.id ? 0.15 : 0.06,
                  weight: selectedDealer?.id === dealer.id ? 2 : 1,
                }}
              />
              <Marker
                position={[dealer.lat, dealer.lng]}
                icon={selectedDealer?.id === dealer.id ? selectedIcon : activeIcon}
                eventHandlers={{
                  click: () => setSelectedDealer(dealer),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-semibold text-gray-900 text-sm">{dealer.company_name}</div>
                    <div className="text-xs text-gray-500 mt-1 mb-2">{dealer.city}, {dealer.state}</div>
                    <div className="space-y-1 text-xs text-gray-600">
                      {dealer.phone && (
                        <div className="flex items-center gap-1.5">
                          <FiPhone className="w-3 h-3" />
                          {dealer.phone}
                        </div>
                      )}
                      {dealer.email && (
                        <div className="flex items-center gap-1.5">
                          <FiMail className="w-3 h-3" />
                          {dealer.email}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <FiPackage className="w-3 h-3" />
                        {dealer.shed_count} sheds in inventory
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                      Coverage: {dealer.radius_miles} mile radius
                    </div>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default DealerMap;
